// models/InvitationService.js
import { db } from '../services/firebase';
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  serverTimestamp,
  limit,
  orderBy
} from 'firebase/firestore';
import { sendEmail } from './emailService';

class InvitationService {
  // Create a new invitation
  async createInvitation(orgId, orgName, email, name, phone, role = 'member', invitedBy = "Admin") {
    try {
      const invitationRef = collection(db, 'invitations');
      
      // Generate a unique code for the invitation
      const code = Math.random().toString(36).substring(2, 10) + Date.now().toString(36);
      
      // Create the invitation document
      const invitationData = {
        organizationId: orgId,
        organizationName: orgName,
        name: name,
        phone: phone,
        email: email.toLowerCase(),
        role,
        code,
        createdAt: serverTimestamp(),
        status: 'pending',
        invitedBy: invitedBy || ""
      };
      
      const docRef = await addDoc(invitationRef, invitationData);
      
      // Send the invitation email
      await this.sendInvitationEmail({
        ...invitationData,
        id: docRef.id
      });
      
      return {
        id: docRef.id,
        ...invitationData,
        code
      };
    } catch (error) {
      console.error('Error creating invitation:', error);
      throw error;
    }
  }
  
  // Send invitation email
  async sendInvitationEmail(invitation) {
    try {
      // Create the invitation link
      const encodedEmail = encodeURIComponent(invitation.email);
      const invitationLink = `https://resolut.co.in/register?code=${invitation.code}&email=${encodedEmail}`;
      
      // Format the email content
      const emailText = `
  Hi ${invitation.email.split('@')[0]},
  
  You've been invited to join ${invitation.organizationName} as a ${invitation.role}.
  
  Please use the following link to create your account and accept the invitation:
  ${invitationLink}
  
  This link will expire in 7 days.
  
  Best regards,
  The Resolut Team
      `;
    
      const result = await sendEmail({
        email: invitation.email,       // 'email' parameter that the Cloud Function expects
        text: emailText,               // 'text' parameter that the Cloud Function expects
        subject: `Invitation to join ${invitation.organizationName} on Resolut`
      });
      
      console.log("Invitation email sent successfully:", result.data);
      return true;
    } catch (error) {
      console.error('Error sending invitation email:', error);
      throw error;
    }
  }
  
  // Get an invitation by code
  async getInvitationByCode(code) {
    try {
      const invitationsRef = collection(db, 'invitations');
      const q = query(invitationsRef, where('code', '==', code), limit(1));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }
      
      const doc = querySnapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data()
      };
    } catch (error) {
      console.error('Error getting invitation by code:', error);
      throw error;
    }
  }
  
  // Get invitations for an organization
  async getOrganizationInvitations(orgId) {
    try {
      const invitationsRef = collection(db, 'invitations');
      const q = query(
        invitationsRef, 
        where('organizationId', '==', orgId),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const invitations = [];
      
      querySnapshot.forEach(doc => {
        invitations.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      return invitations;
    } catch (error) {
      console.error('Error getting organization invitations:', error);
      throw error;
    }
  }
  
  // Accept an invitation
  async acceptInvitation(invitationId, userId) {
    try {
      // Get the invitation
      const invitationRef = doc(db, 'invitations', invitationId);
      const invitationDoc = await getDoc(invitationRef);
      
      if (!invitationDoc.exists()) {
        throw new Error('Invitation not found');
      }
      
      const invitation = invitationDoc.data();
      
      // Check if the invitation is pending
      if (invitation.status !== 'pending') {
        throw new Error('Invitation has already been used or expired');
      }
      
      // Update the user document with organization info
      const userRef = doc(db, 'users', userId);

      const timestamp = new Date().toISOString();

      await updateDoc(userRef, {
        organizationId: invitation.organizationId,
        organizationName: invitation.organizationName,
        organizationRole: invitation.role,
        accountType: 'employer',
        joinedAt: timestamp
      });
      
      // Update the organization document to add the user as a member
      const orgRef = doc(db, 'organizations', invitation.organizationId);
      const orgDoc = await getDoc(orgRef);
      
      if (!orgDoc.exists()) {
        throw new Error('Organization not found');
      }
      
      const org = orgDoc.data();
      
      // Add the user to the members array
      const updatedMembers = [...(org.members || [])];
      
      // Check if user is already a member
      const existingMemberIndex = updatedMembers.findIndex(m => m.uid === userId);
      
      if (existingMemberIndex === -1) {
        // Add as new member
        updatedMembers.push({
          uid: userId,
          email: invitation.email,
          name: invitation.name || "",
          phone: invitation.phone || "",
          role: invitation.role,
          joinedAt: timestamp
        });
      } else {
        // Update existing member
        updatedMembers[existingMemberIndex] = {
          ...updatedMembers[existingMemberIndex],
          role: invitation.role,
          joinedAt: timestamp
        };
      }
      
      await updateDoc(orgRef, {
        members: updatedMembers
      });
      
      // Update the invitation status
      await updateDoc(invitationRef, {
        status: 'accepted',
        acceptedBy: userId,
        acceptedAt: serverTimestamp()
      });
      
      return {
        organizationId: invitation.organizationId,
        organizationName: invitation.organizationName,
        role: invitation.role
      };
    } catch (error) {
      console.error('Error accepting invitation:', error);
      throw error;
    }
  }
  
  // Resend an invitation
  async resendInvitation(invitationId) {
    try {
      const invitationRef = doc(db, 'invitations', invitationId);
      const invitationDoc = await getDoc(invitationRef);
      
      if (!invitationDoc.exists()) {
        throw new Error('Invitation not found');
      }
      
      const invitation = {
        id: invitationDoc.id,
        ...invitationDoc.data()
      };
      
      // Check if the invitation is still pending
      if (invitation.status !== 'pending') {
        throw new Error('Cannot resend an invitation that has been accepted or expired');
      }
      
      // Send the invitation email again
      await this.sendInvitationEmail(invitation);
      
      // Update the invitation with the resend timestamp
      await updateDoc(invitationRef, {
        lastResent: serverTimestamp()
      });
      
      return true;
    } catch (error) {
      console.error('Error resending invitation:', error);
      throw error;
    }
  }
  
  // Cancel/revoke an invitation
  async cancelInvitation(invitationId) {
    try {
      const invitationRef = doc(db, 'invitations', invitationId);
      
      await updateDoc(invitationRef, {
        status: 'cancelled',
        cancelledAt: serverTimestamp()
      });
      
      return true;
    } catch (error) {
      console.error('Error cancelling invitation:', error);
      throw error;
    }
  }
  
  // Check if an email has a pending invitation
  async checkPendingInvitation(email) {
    try {
      const invitationsRef = collection(db, 'invitations');
      const q = query(
        invitationsRef,
        where('email', '==', email.toLowerCase()),
        where('status', '==', 'pending'),
        limit(1)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }
      
      const doc = querySnapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data()
      };
    } catch (error) {
      console.error('Error checking pending invitation:', error);
      throw error;
    }
  }
}

export default new InvitationService();